/**
 * @fileoverview React Error Boundary component.
 */

import type { ErrorInfo, ReactNode } from 'react';
import { Component } from 'react';

import { Button } from '../../ui/Button';
import { Icon } from '../../ui/Icon';
import { reloadOnceForErrorRecovery } from '../../../utils/reloadOnError';
import styles from './ErrorBoundary.module.css';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/**
 * Error boundary component to catch React errors.
 *
 * @example
 * ```tsx
 * <ErrorBoundary>
 *   <App />
 * </ErrorBoundary>
 * ```
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  override componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error('Error boundary caught an error:', error, errorInfo);

    reloadOnceForErrorRecovery('react-error');
  }

  handleReset = (): void => {
    window.location.reload();
  };

  override render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className={styles.container}>
          <div className={styles.content}>
            <Icon
              name="exclamation-triangle-fill"
              size="xl"
              {...(styles.icon ? { className: styles.icon } : {})}
            />
            <h1 className={styles.title}>ಏನೋ ತಪ್ಪಾಗಿದೆ</h1>
            <p className={styles.message}>ಕ್ಷಮಿಸಿ, ಅಪ್ಲಿಕೇಶನ್‌ನಲ್ಲಿ ದೋಷ ಸಂಭವಿಸಿದೆ.</p>
            {this.state.error && (
              <details className={styles.details}>
                <summary>ತಾಂತ್ರಿಕ ವಿವರಗಳು</summary>
                <pre className={styles.errorText}>{this.state.error.message}</pre>
              </details>
            )}
            <div className={styles.actions}>
              <Button variant="primary" onClick={this.handleReset}>
                <Icon name="arrow-clockwise" size="sm" />
                ಮತ್ತೆ ಪ್ರಯತ್ನಿಸಿ
              </Button>
              <Button variant="outline" onClick={() => (window.location.href = '/')}>
                <Icon name="house-fill" size="sm" />
                ಮುಖಪುಟಕ್ಕೆ ಹೋಗಿ
              </Button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
